import { useEffect } from 'react';
import { useFormUrl } from '@/hooks/useFormUrl';
import Breadcrumb from '@/components/base/Breadcrumb';
import { brandInfo, testimonials } from '@/mocks/data';

function ContactForm() {
  const formUrl = useFormUrl();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.elements.namedItem('phone_alt') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      const alertEl = document.getElementById('form-alert');
      if (alertEl) {
        alertEl.textContent = '感谢您的留言，我们会尽快回复！';
        alertEl.className = 'text-sm text-green-600 mt-3 text-center';
        alertEl.style.display = 'block';
      }
      return;
    }

    if (!formUrl) {
      const alertEl = document.getElementById('form-alert');
      if (alertEl) {
        alertEl.textContent = '表单暂不可用，请通过邮箱联系我们。';
        alertEl.className = 'text-sm text-red-500 mt-3 text-center';
        alertEl.style.display = 'block';
      }
      return;
    }

    const formData = new FormData(form);
    try {
      const res = await fetch(formUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const text = await res.text();
      let serverMsg = '';
      try {
        const parsed = JSON.parse(text);
        serverMsg = parsed?.meta?.message || parsed?.message || '';
      } catch {
        serverMsg = text;
      }

      const alertEl = document.getElementById('form-alert');
      if (!alertEl) return;

      if (res.ok) {
        alertEl.textContent = '感谢您的留言，我们会尽快回复！';
        alertEl.className = 'text-sm text-green-600 mt-3 text-center';
        alertEl.style.display = 'block';
        form.reset();
      } else {
        alertEl.textContent = serverMsg || '提交失败，请稍后重试';
        alertEl.className = 'text-sm text-red-500 mt-3 text-center';
        alertEl.style.display = 'block';
      }
    } catch {
      const alertEl = document.getElementById('form-alert');
      if (alertEl) {
        alertEl.textContent = '网络错误，请稍后重试';
        alertEl.className = 'text-sm text-red-500 mt-3 text-center';
        alertEl.style.display = 'block';
      }
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5" data-readdy-form>
      <div>
        <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-800 mb-1.5">
          您的姓名 <span className="text-primary-500">*</span>
        </label>
        <input
          id="contact-name"
          name="name"
          type="text"
          required
          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
          placeholder="请输入您的姓名"
        />
      </div>
      <div>
        <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-800 mb-1.5">
          电子邮箱 <span className="text-primary-500">*</span>
        </label>
        <input
          id="contact-email"
          name="email"
          type="email"
          required
          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
          placeholder="请输入您的电子邮箱"
        />
      </div>
      <div>
        <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-800 mb-1.5">
          留言内容 <span className="text-primary-500">*</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={4}
          className="w-full px-4 py-2.5 text-sm rounded-lg border border-background-300 bg-background-50 text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
          placeholder="请输入您的留言内容（不超过500字）"
        ></textarea>
      </div>
      {/* Anti-spam honeypot */}
      <div className="hp-field">
        <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
      </div>
      <button
        type="submit"
        className="w-full px-6 py-3 text-sm font-semibold rounded-full bg-primary-500 text-background-50 cursor-pointer transition-all duration-200 hover:bg-primary-600 active:scale-95 whitespace-nowrap"
      >
        发送留言
      </button>
      <div id="form-alert" className="text-sm text-center mt-3" style={{ display: 'none' }}></div>
    </form>
  );
}

export default function About() {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  return (
    <div className="pt-16">
      <Breadcrumb items={[{ label: '关于我们' }]} />

      {/* Brand Intro */}
      <section className="w-full px-4 md:px-6 py-12 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 mb-3">关于蜜桃传媒</h1>
            <p className="text-sm text-foreground-500 max-w-2xl mx-auto">{brandInfo.slogan}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <div>
              <img
                src="https://readdy.ai/api/search-image?query=Modern%20media%20production%20office%20with%20warm%20lighting%2C%20creative%20team%20workspace%2C%20professional%20video%20editing%20suite%2C%20cinematic%20equipment%2C%20collaborative%20atmosphere%2C%20corporate%20headquarters%20interior%2C%20elegant%20design&width=800&height=600&seq=about-brand-01&orientation=landscape"
                alt="蜜桃传媒办公环境"
                className="w-full rounded-lg object-cover aspect-[4/3]"
              />
            </div>
            <div>
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-4">品牌简介</h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">{brandInfo.description}</p>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center shrink-0 rounded-full bg-primary-100 mt-0.5">
                    <i className="ri-focus-2-line text-xs text-primary-500"></i>
                  </div>
                  <div>
                    <strong className="text-sm text-foreground-950">使命：</strong>
                    <span className="text-sm text-foreground-600">{brandInfo.mission}</span>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center shrink-0 rounded-full bg-primary-100 mt-0.5">
                    <i className="ri-eye-line text-xs text-primary-500"></i>
                  </div>
                  <div>
                    <strong className="text-sm text-foreground-950">愿景：</strong>
                    <span className="text-sm text-foreground-600">{brandInfo.vision}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-5">
                {brandInfo.values.map((v) => (
                  <span key={v} className="px-3 py-1 text-xs font-medium rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                    {v}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-14">
            {brandInfo.stats.map((stat) => (
              <div key={stat.label} className="text-center p-6 rounded-lg bg-background-100/70">
                <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1 whitespace-nowrap">{stat.value}</div>
                <div className="text-xs text-foreground-500 whitespace-nowrap">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Reputation */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100/60">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">用户口碑</h2>
            <p className="text-sm text-foreground-500">听听用户对蜜桃传媒的真实评价</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div key={t.id} className="p-6 rounded-lg bg-background-50">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm text-accent-500"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">{t.content}</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                    <span className="text-sm font-semibold text-primary-500">{t.name.charAt(0)}</span>
                  </div>
                  <div>
                    <div className="text-sm font-medium text-foreground-950 whitespace-nowrap">{t.name}</div>
                    <div className="text-xs text-foreground-500 whitespace-nowrap">{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">联系我们</h2>
            <p className="text-sm text-foreground-500">有任何问题或合作意向，欢迎随时与我们取得联系</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center shrink-0 rounded-lg bg-primary-100">
                  <i className="ri-mail-line text-lg text-primary-500"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">电子邮箱</h3>
                  <p className="text-sm text-foreground-600">{brandInfo.contact.email}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center shrink-0 rounded-lg bg-primary-100">
                  <i className="ri-phone-line text-lg text-primary-500"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">客服电话</h3>
                  <p className="text-sm text-foreground-600">{brandInfo.contact.phone}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center shrink-0 rounded-lg bg-primary-100">
                  <i className="ri-map-pin-line text-lg text-primary-500"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">公司地址</h3>
                  <p className="text-sm text-foreground-600">{brandInfo.contact.address}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 flex items-center justify-center shrink-0 rounded-lg bg-primary-100">
                  <i className="ri-wechat-line text-lg text-primary-500"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-950 mb-1">微信公众号</h3>
                  <p className="text-sm text-foreground-600">{brandInfo.contact.wechat}</p>
                </div>
              </div>
            </div>
            <div className="p-6 rounded-lg bg-background-100/70">
              <h3 className="text-sm font-semibold text-foreground-950 mb-4">在线留言</h3>
              <ContactForm />
            </div>
          </div>
        </div>
      </section>

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'AboutPage',
            name: '关于蜜桃传媒',
            description: brandInfo.description,
            url: 'https://www.mitaomedia.com/about',
            isPartOf: {
              '@type': 'WebSite',
              name: '蜜桃传媒',
              url: 'https://www.mitaomedia.com',
            },
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://www.mitaomedia.com',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: '关于我们',
                },
              ],
            },
          }),
        }}
      />
    </div>
  );
}